import React, { useContext } from 'react';
import { graphql, useFragment } from 'react-relay';
import DashboardIcon from '@atlaskit/icon/core/dashboard';
import {
	SECTION_ITEM_RECENT,
	SECTION_ITEM_STARRED,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { ENTITY_ID } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { Nav4MenuLinkItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { useNestedItemDragAndDrop } from '@atlassian/navigation-drag-and-drop';
import { AnalyticContext } from '../../controllers/analytic-context/index.tsx';
import { useDashboardReorderingIds } from '../../controllers/use-dashboard-reordering-ids.tsx';
import { DashboardPopupMenuRefactored } from '../dashboard-popup-menu/DashboardPopupMenuRefactored.tsx';
import type {
	DashboardMenuItemRefactored$key,
	DashboardMenuItemRefactored$data,
} from './__generated__/DashboardMenuItemRefactored.graphql.ts';

type DashboardMenuItemProps = {
	queryRef: DashboardMenuItemRefactored$key;
	currentItems?: Array<{ id: string; label: string }>;
	currentOrder?: Array<{ menuId: string; visible: boolean }>;
	item?: {
		id: string;
		label: string;
	};
};

export function DashboardMenuItemRefactored({
	queryRef,
	currentItems,
	currentOrder,
	item,
}: DashboardMenuItemProps): React.JSX.Element | null {
	const data = useFragment<DashboardMenuItemRefactored$key>(
		graphql`
			fragment DashboardMenuItemRefactored on JiraDashboard {
				id @required(action: THROW)
				title @required(action: THROW)
				dashboardId @required(action: THROW)
				favouriteValue {
					isFavourite
				}
				...DashboardPopupMenuRefactored
			}
		`,
		queryRef,
	);

	const { fireUIAnalyticForDashboardItems } = useContext(AnalyticContext);

	if (!data) {
		return null;
	}

	const dashboardUrl = `/jira/dashboards/${data.dashboardId}`;

	if (data?.favouriteValue?.isFavourite) {
		return (
			<DraggableDashboardMenuItem
				data={data}
				dashboardUrl={dashboardUrl}
				currentItems={currentItems}
				currentOrder={currentOrder}
				item={item}
			/>
		);
	}

	return (
		<Nav4MenuLinkItem
			href={dashboardUrl}
			menuId={ENTITY_ID.DASHBOARD(String(data.dashboardId))}
			actionsOnHover={<DashboardPopupMenuRefactored dashboardData={data} />}
			elemBefore={<DashboardIcon label="" color="currentColor" />}
			onClick={() => {
				fireUIAnalyticForDashboardItems({
					itemId: String(data.dashboardId),
					section: data?.favouriteValue?.isFavourite ? SECTION_ITEM_STARRED : SECTION_ITEM_RECENT,
				});
			}}
		>
			{data.title}
		</Nav4MenuLinkItem>
	);
}

type DraggableDashboardMenuItemProps = {
	data: DashboardMenuItemRefactored$data;
	dashboardUrl: string;
	currentItems?: Array<{ id: string; label: string }>;
	currentOrder?: Array<{ menuId: string; visible: boolean }>;
	item?: {
		id: string;
		label: string;
	};
};

const DraggableDashboardMenuItem = ({
	data,
	dashboardUrl,
	currentItems,
	currentOrder,
	item,
}: DraggableDashboardMenuItemProps) => {
	const { fireUIAnalyticForDashboardItems } = useContext(AnalyticContext);
	const { entityAri, ownerAri, group } = useDashboardReorderingIds();
	const dragAndDropState = useNestedItemDragAndDrop({
		entityAri,
		ownerAri,
		data: {
			id: data.id,
			label: data.title,
		},
		group,
		item: undefined,
		isExpandable: false,
		isExpandOrFlyoutOpen: false,
		id: data.id,
		isDragAndDropEnabled: true,
		onExpandFlyoutToggle: () => {
			// No op for dashboard menu item
		},
	});
	return (
		<>
			<Nav4MenuLinkItem
				href={dashboardUrl}
				menuId={ENTITY_ID.DASHBOARD(String(data.id))}
				actionsOnHover={
					<DashboardPopupMenuRefactored
						dashboardData={data}
						currentItems={currentItems}
						currentOrder={currentOrder}
						item={item}
					/>
				}
				elemBefore={<DashboardIcon label="" color="currentColor" />}
				onClick={() => {
					fireUIAnalyticForDashboardItems({
						itemId: String(data.dashboardId),
						section: data?.favouriteValue?.isFavourite ? SECTION_ITEM_STARRED : SECTION_ITEM_RECENT,
					});
				}}
				ref={dragAndDropState?.draggableRef}
				visualContentRef={dragAndDropState?.dropTargetRef}
				dropIndicator={dragAndDropState?.dropIndicator}
				isDragging={dragAndDropState?.isDragging}
				hasDragIndicator
			>
				{data.title}
			</Nav4MenuLinkItem>
			{dragAndDropState && dragAndDropState.maybeDragPreview}
		</>
	);
};
